// @flow weak
// vendor libs
import CONST from 'app/common/lib/Const';
import context from 'app/common/lib/context';
import contextEvent from 'app/common/lib/ContextEvent';
import DomUtil from 'app/common/lib/DomUtil';
import experiments from 'app/common/lib/experiments';
import P from 'app/common/lib/P';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import sendActiveUserPing from 'app/common/lib/react/sendActiveUserPing';
import store from 'app/common/redux/store/singleton';
import throttle from 'lodash/throttle';
import timer from 'app/common/lib/timer';
import timeSpentManager from 'app/common/lib/react/TimeSpentManager';
import urlUtil from 'app/common/lib/urlUtil';
import uuidV4 from 'uuid/v4';
import { instance as pubSub } from 'app/common/lib/PubSub';
import { once } from 'app/common/lib/DebounceUtil';
import Impressions, { LOW_PRIORITY } from 'app/packages/impression-logging';

let contextLogger = {};

// Check if we should console log debug statements
const shouldShowLogStatements =
  urlUtil.getQueryStringParams() && urlUtil.getQueryStringParams().debug_context_log;

/**
 * Returns a function to log debugging information if query param supplied,
 * otherwise returns a no-op
 */
const customConsole: any = shouldShowLogStatements
  ? console
  : {
      error: () => {},
      info: () => {},
      log: () => {},
      group: () => {},
      groupEnd: () => {},
    };

// Some browsers don't have console.group
// eslint-disable-next-line no-console
if (!console.group) {
  // eslint-disable-next-line no-console
  customConsole.group = (...args) => console.log(...args);
  customConsole.groupEnd = () => {};
}

/**
 * Get the current timestamp in nanoseconds. Converts ms to ns by
 * multiplying the timestamp by 10^6.
 * @return {number}
 */
const currentNs = () => Date.now() * 1000000;

/**
 * Logger that handles sending context logging information to the server. This
 * buffers all events and then flushes them every FLUSH_INTERVAL_SECONDS if
 * there are events to log.
 * @constructor
 */
const ContextLogger = function() {
  /**
   * Buffer of events to log.
   * @type {!Array.<!ContextEvent>}
   * @private
   */
  this._events = [];

  /**
   * The current view context.
   * @type {ContextEvent}
   * @private
   */
  this._currentViewContext = null;

  /**
   * The previous view context.
   * @type {ContextEvent}
   * @private
   */
  this._previousViewContext = null;

  /**
   * Timeout variable for the long flush timeout.
   * @type {?number}
   * @private
   */
  this._logTimeoutLong = null;

  /**
   * Timeout variable for the short flush timeout.
   * @type {?number}
   * @private
   */
  this._logTimeoutShort = null;

  /**
   * Dict that maps elements with logging around module view events
   * to their log parameters.
   *
   * @type {Object}
   * @private
   **/
  this._moduleViewElements = {};

  /**
   * Dict that maps elements to scroll listeners on them
   *
   * @type {Object}
   * @private
   **/
  this._scrollListeners = {};

  /**
   * Last PIN_CLICKTHROUGH (start) event that has not ended
   *
   * @type {Object}
   * @private
   **/
  this._clickthroughStartEvent = null;

  /**
   * Stores the current UTC date in order to correctly count users as active when their session
   * crosses the UTC dateline
   *
   * @type {Date}
   * @private
   **/
  this._utcDate = new Date();

  /*
    Since iframes are not children of `window`, when the user clicks into an iframe (e.g. a YouTube
    video pin closeup), `window` will blur but the user is still viewing the page, so we should
    continue to log impressions. These iframes are typically cross-origin, so we cannot directly
    access their focus state. This hack in `handleLeave` blurs the iframe (video iframes only!) on
    click, so the user's action occurs (e.g. playing the video) but we continue to keep focus on
    `window`, thereby correctly pausing/resuming impressions tracking on a "real" `window` blur
    (e.g. clicking to another tab).
  */

  const handleLeave = (forceSync: ?boolean) => {
    const { activeElement } = document;
    const iframeIsActiveElement = activeElement && activeElement.id === CONST.VIDEO_IFRAME_ID;

    // activeElement check here is superfluous but keeps Flow happy
    if (iframeIsActiveElement && activeElement) {
      // timeout necessary to not blur the iframe too quickly
      setTimeout(() => activeElement.blur(), 0);
      return;
    }

    // See more context re: priority in app/packages/impression-logging/Impressions.js
    Impressions.pause(LOW_PRIORITY);
    this.flushEvents(forceSync);
  };

  const handleClose = once(() => handleLeave(true));

  const handleResume = () => {
    // See more context re: priority in app/packages/impression-logging/Impressions.js
    Impressions.resume(LOW_PRIORITY);
  };

  // Flush events before the window is unloaded.
  window.addEventListener('beforeunload', handleClose);
  // because ios safari doesn't support beforeunload
  window.addEventListener('pagehide', handleClose);
  window.addEventListener('focus', handleResume);
  window.addEventListener('blur', handleLeave);
};

// TODO(nadine): Investigate flushing opportunistically when the browser is idle.
/**
 * The minimum number of ms between event logging requests to the server.
 * @type {number}
 */
ContextLogger.FLUSH_INTERVAL_LONG = 10000;

ContextLogger.FLUSH_INTERVAL_SHORT = 300;

/*
 * We don't want to batch some events
 * Note(jie): ViewType.LOGIN has low volume and large data loss for mobile web one tap login, so
 * adding to fast flush.
 * @type {array}
 */
ContextLogger.EVENTS_FAST_FLUSH = [PCONST.thrift.ViewType.LOGIN, PCONST.thrift.ViewType.PIN];

ContextLogger.IMPRESSION_NAMES = {
  [PCONST.thrift.EventType.ARTICLE_IMPRESSION_ONE_PIXEL]: 'articleImpressions',
  [PCONST.thrift.EventType.PIN_IMPRESSION_ONE_PIXEL]: 'pinImpressions',
  [PCONST.thrift.EventType.BOARD_IMPRESSION_ONE_PIXEL]: 'boardImpressions',
  [PCONST.thrift.EventType.SEARCH_IMPRESSION_ONE_PIXEL]: 'searchImpressions',
  [PCONST.thrift.EventType.USER_IMPRESSION_ONE_PIXEL]: 'userImpressions',
};

/** Accounts for UTC date cross over for active user ping. When a user has generated context logs across multiple
 * UTC days, we send an addition Active User ping to ensure they are counted as a DAU.
 **/
ContextLogger.prototype._handleDatelineCrossover = function() {
  const now = new Date();
  if (this._utcDate.getUTCDate() !== now.getUTCDate()) {
    this._utcDate = now;
    sendActiveUserPing();
  }
};

/**
 * Add the event to the buffer to be logged. Any non-view events will have
 * the current view context tacked on to it. Doesn't log anything if the
 * view context is unknown.
 *
 * @param {!ContextEvent} event New context event
 * @param {Object=} evt jQuery event that caused the context event.
 */
ContextLogger.prototype.addEvent = function(event, evt) {
  const eventType = event.getEventType();

  if (eventType === null || eventType === undefined) {
    // Don't log any events that don't have an eventType
    return;
  }

  this._handleDatelineCrossover();

  if (evt) {
    this._lastLoggedEvent = evt.originalEvent;
  }

  if (eventType === PCONST.thrift.EventType.VIEW) {
    if (this._currentViewContext) {
      this._previousViewContext = this._currentViewContext;
    }
    this._currentViewContext = contextEvent.fromPlainObject({});
    this._currentViewContext.copyViewContext(event);
  } else if (
    [
      PCONST.thrift.EventType.TIMED_APP_FOREGROUND,
      PCONST.thrift.EventType.TIMED_APP_BACKGROUND,
      PCONST.thrift.EventType.TIMED_PAIR_BEGIN,
      PCONST.thrift.EventType.TIMED_PAIR_END,
    ].includes(eventType)
  ) {
    // Don't copyViewContext for TIMED_APP_FORE/BACKGROUND events
  } else if (
    [PCONST.thrift.EventType.TRANSACTION_START, PCONST.thrift.EventType.TRANSACTION_END].includes(
      eventType,
    )
  ) {
    // Don't copyViewContext for transaction related events
  } else {
    if (!this._currentViewContext) {
      // Don't log actions that don't have a view context.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.trace(
          'addViewToContext() was not called, cannot log non-view event of type ' +
            eventType +
            '. ' +
            'See ContextLogger.js for more information.',
        );
      }
      return;
    }

    // if event already has a view type and view parameter don't set it to the
    // page's view
    if (!event.getViewType() || !event.getViewParameter()) {
      event.copyViewContext(this._currentViewContext);
    }

    if (ContextLogger.IMPRESSION_NAMES[eventType]) {
      const eventTypeName = ContextLogger.IMPRESSION_NAMES[eventType];
      const [lastEvent] = this._events.slice(-1);
      if (
        lastEvent &&
        lastEvent.hasSameViewContext(event) &&
        lastEvent.getEventType() === eventType
      ) {
        lastEvent.appendImpressions(eventTypeName, event.getImpressions(eventTypeName));
        return;
      }
    }
  }

  event.setTime(currentNs());
  event.setBrowser(context.instance.browser_type);
  event.setClientUUID(uuidV4());
  this._events.push(event);
  customConsole.log('addEvent', event);
  if (
    ContextLogger.EVENTS_FAST_FLUSH.includes(event.getViewType()) &&
    !this._logTimeoutShort &&
    eventType === PCONST.thrift.EventType.VIEW
  ) {
    this._logTimeoutShort = timer.setTimeout(
      this.flushEvents.bind(this, false, true),
      ContextLogger.FLUSH_INTERVAL_SHORT,
    );
  } else if (!this._logTimeoutLong) {
    let longFlushInterval;
    const group =
      experiments.getInstance().v2ActivateExperiment('web_context_log_reliability') || '';
    switch (group) {
      case 'enabled_15':
        longFlushInterval = 15000;
        break;
      case 'enabled_5':
        longFlushInterval = 5000;
        break;
      case 'enabled_2':
        longFlushInterval = 2000;
        break;
      default:
        longFlushInterval = ContextLogger.FLUSH_INTERVAL_LONG;
        break;
    }

    this._logTimeoutLong = timer.setTimeout(this.flushEvents.bind(this), longFlushInterval);
  }

  if (eventType === PCONST.thrift.EventType.PIN_CLICKTHROUGH) {
    // For every PIN_CLICKTHROUGH, clone the event, set the clone to DESTINATION_VIEW and add it for logging.
    // DESTINATION_VIEW events occur when a pinner is taken to and views the website behind a pin.
    // On web this maps to every PIN_CLICKTHROUGH, but on Android/iOS a PIN_CLICKTHROUGH does not guarantee a pinner
    // has been taken to and is viewing a pin's website (1-tap promoted pins for example).
    const destinationEvent = contextEvent.clone(event);
    destinationEvent.setEventType(PCONST.thrift.EventType.DESTINATION_VIEW);
    this.addEvent(destinationEvent);

    // v2a method of calculating duration
    this._clickthroughStartEvent = event;

    // TimeSpent method of calculating duration
    timeSpentManager.storeClickthroughProperties({
      view_type: event.getViewType(),
      view_parameter: event.getViewParameter(),
      component: event.getComponent(),
      element: event.getElement(),
      object_id_str: event.getObjectIdStr(),
      view_data: event.getViewData(),
      aux_data: event.getAuxData(),
      clientTrackingParams: event.getClientTrackingParams(),
    });
  }
};

/**
 * Log a View Event
 *
 * @param {!PCONST.thrift.ViewType} viewType
 * @param {?PCONST.thrift.ViewParameterType} viewParameter
 */
ContextLogger.prototype.logCustomViewEvent = function(viewType, viewParameter) {
  const eventDict = {
    event_type: PCONST.thrift.EventType.VIEW,
    view_type: viewType,
    view_parameter: viewParameter,
  };
  const _contextEvent = contextEvent.fromPlainObject(eventDict);
  this.addEvent(_contextEvent);
};

/**
 * @param {Object} container the container element for tracking
 * @param {Object} element the element to track
 * @param {boolean} isModuleView whether we are logging module view events (as opposed to view_x)
 * @private
 */
ContextLogger.prototype._addElementToScrollListener = function(container, element, isModuleView) {
  // TODO(jliu): className is brittle. Consider whether we can use other jquery object identifiers
  const scrollElement = container.getScrollableElement()[0];
  let scrollId;

  if (scrollElement === window) {
    scrollId = 'window';
  } else {
    scrollId = scrollElement.className;
  }

  if (this._scrollListeners[scrollId]) {
    this._scrollListeners[scrollId].elements.push(element);
  } else {
    this._scrollListeners[scrollId] = { elements: [element] };

    const throttledListener = throttle(() => {
      this._scrollListeners[scrollId].elements.forEach((element) => {
        this._checkInViewport(element, isModuleView);
      });
    }, 16);

    this._scrollListeners[scrollId].listener = container.listen(
      container.getScrollableElement(),
      PCONST.EVENT_SCROLL,
      throttledListener,
    );
  }
};

/**
 * @param {Object} container the container element for tracking
 * @param {Object} element the element to track
 * @private
 */
ContextLogger.prototype._removeElementFromScrollListener = function(container, element) {
  const scrollElement = container.getScrollableElement()[0];
  let scrollId;

  if (scrollElement === window) {
    scrollId = 'window';
  } else {
    scrollId = scrollElement.className;
  }

  const scrollListener = this._scrollListeners[scrollId];
  if (!scrollListener) {
    return;
  }

  scrollListener.elements.splice(scrollListener.elements.indexOf(element), 1);
  if (scrollListener.elements.length === 0) {
    container.removeListener(scrollListener.listener);
    delete this._scrollListeners[scrollId];
  }
};

/**
 * Set up context logging for PIN_CARD_VIEW events on a given element
 * with a null ElementType
 *
 * Note: PIN_CARD_VIEW is the EventType we are currently using to log module
 * view events
 *
 * @param {Object} element the module element to track
 * @param {PCONST.thrift.ComponentType} componentType the ComponentType to log
 * @param {Object=} auxData any auxData to log
 */
ContextLogger.prototype.setupModuleViewContextLogging = function(element, componentType, auxData) {
  this._moduleViewElements[element.cid] = {
    elementType: null,
    componentType,
    auxData,
    hasBeenViewed: false,
  };

  if (DomUtil.isElementInViewport(element.el)) {
    // Log a PIN_CARD_VIEW event if the element is already in the viewport
    this._logModuleViewEvent(element, PCONST.thrift.EventType.PIN_CARD_VIEW);
  } else {
    // Log a PIN_CARD_VIEW event as soon as the element scrolls into the viewport
    this._addElementToScrollListener(element, element, true);
  }

  const elementOnRemovedFromDom = element.onRemovedFromDom;
  element.onRemovedFromDom = function() {
    delete this._moduleViewElements[element.cid];
    this._removeElementFromScrollListener(element, element);
    elementOnRemovedFromDom();
    element.onRemovedFromDom = elementOnRemovedFromDom;
  }.bind(this);
};

/**
 * Set up context logging for VIEW_BEGIN and VIEW_END events on a given element
 * with a null ElementType
 *
 * @param {Object} mod the module containing the element to track
 * @param {Object} element the element to track to log VIEW_BEGIN and VIEW_END events - can be a module or a dom element
 * @param {PCONST.thrift.ComponentType} componentType the ComponentType to log
 * @param {Object=} auxData any auxData to log
 * @param {Object=} scrollableElementToTrack optional scrollable child of element containing children to track
 *                                           as cards viewed - can be a module or a dom element
 */
ContextLogger.prototype.setupViewContextLogging = function(
  mod,
  element,
  componentType,
  auxData,
  scrollableElementToTrack,
) {
  // If element is not a full module, wrap it into a module-like object
  // to reduce forking logic later
  if (!element.cid && element[0].className) {
    element = {
      cid: element[0].className,
      el: element,
      parentMod: mod,
    };
  } else {
    element.parentMod = element;
  }

  this._moduleViewElements[element.cid] = {
    elementType: null,
    componentType,
    auxData,
    isVisible: false,
  };

  if (scrollableElementToTrack) {
    this._moduleViewElements[element.cid].auxData.cards_viewed = 0;
    if (scrollableElementToTrack.el) {
      // standardize scrollableElementToTrack for modules and non-modules
      scrollableElementToTrack = scrollableElementToTrack.$el;
    }

    this._trackItemsViewed(mod, element, scrollableElementToTrack, componentType);
    this._calculateItemsViewed(element, scrollableElementToTrack);
  }

  // Log an initial VIEW_BEGIN event if the element is in the viewport
  this._checkInViewport(element, false);

  // Log VIEW_END and VIEW_BEGIN events as the element scrolls in and out of the viewport
  this._addElementToScrollListener(mod, element, false);

  // Log VIEW_END and VIEW_BEGIN events as the user opens and closes modals over the element
  // TODO(jliu): This pubsub channel is currently TUTORIAL because of existing ModalManager logic
  // but ideally we would use a more generic channel for modal events
  let topicToHandlerMap = {};
  topicToHandlerMap[PCONST.PUBSUB_TOPIC_VIEW_CHANGE] = this._onModalViewChange.bind(this, element);
  element.parentMod.pubsubSubscribe(PCONST.PUBSUB_CHANNEL_TUTORIAL, topicToHandlerMap);

  // Log a VIEW_BEGIN event when the window containing the element comes into focus
  topicToHandlerMap = {};
  topicToHandlerMap[PCONST.PUBSUB_TOPIC_WINDOW_VISIBILITY_FOCUS] = function() {
    if (
      this._moduleViewElements[element.cid] &&
      !this._moduleViewElements[element.cid].isVisible &&
      DomUtil.isElementInViewport(element.el)
    ) {
      this._logModuleViewEvent(element, PCONST.thrift.EventType.VIEW_BEGIN);
    }
  }.bind(this);

  // Log a VIEW_END event when the window containing the element goes out of focus
  topicToHandlerMap[PCONST.PUBSUB_TOPIC_WINDOW_VISIBILITY_BLUR] = function() {
    if (this._moduleViewElements[element.cid] && this._moduleViewElements[element.cid].isVisible) {
      this._logModuleViewEvent(element, PCONST.thrift.EventType.VIEW_END);
    }
  }.bind(this);
  element.parentMod.pubsubSubscribe(PCONST.PUBSUB_CHANNEL_SITE, topicToHandlerMap);

  // Log a VIEW_END event when the element is removed from the DOM
  const elementOnRemovedFromDom = element.parentMod.onRemovedFromDom;
  element.parentMod.onRemovedFromDom = function() {
    if (this._moduleViewElements[element.cid] && this._moduleViewElements[element.cid].isVisible) {
      this._logModuleViewEvent(element, PCONST.thrift.EventType.VIEW_END);
    }

    delete this._moduleViewElements[element.cid];
    this._removeElementFromScrollListener(mod, element);
    element.parentMod.pubsubUnsubscribe(PCONST.PUBSUB_CHANNEL_SITE, [
      PCONST.PUBSUB_TOPIC_WINDOW_VISIBILITY_FOCUS,
      PCONST.PUBSUB_TOPIC_WINDOW_VISIBILITY_BLUR,
    ]);
    element.parentMod.pubsubUnsubscribe(PCONST.PUBSUB_CHANNEL_TUTORIAL, [
      PCONST.PUBSUB_TOPIC_VIEW_CHANGE,
    ]);

    elementOnRemovedFromDom();
    element.parentMod.onRemovedFromDom = elementOnRemovedFromDom;
  }.bind(this);

  // Register this handler to set up view logging on future loads of the element
  const elementOnAddedToDom = element.parentMod.onAddedToDom;
  element.parentMod.onAddedToDom = function() {
    this.setupViewContextLogging(mod, element, componentType, auxData);
    elementOnAddedToDom.call(element.parentMod);
    element.parentMod.onAddedToDom = elementOnAddedToDom;
  }.bind(this);
};

/**
 * Setup tracking for the number of items/cards viewed on an element
 * @param {Object} mod the parent module that is scrollable
 * @param {Object} loggingElement the element we are logging on
 * @param {Object} trackedElement the element containing the items/cards to track
 * @private
 */
ContextLogger.prototype._trackItemsViewed = function(mod, loggingElement, trackedElement) {
  const throttledListener = throttle(() => {
    this._calculateItemsViewed.call(this, loggingElement, trackedElement);
  }, CONST.SCROLL_THROTTLE_DELAY_DEFAULT);
  mod.listen(mod.getScrollableElement(), PCONST.EVENT_SCROLL, throttledListener);
};

/**
 * Sets the number of items seen by the viewer
 * @param {Object} loggingElement the element we are logging on
 * @param {Object} trackedElement the element containing the items/cards to track
 * @private
 */
ContextLogger.prototype._calculateItemsViewed = function(loggingElement, trackedElement) {
  for (
    let i = trackedElement.children().length - 1;
    i >= this._moduleViewElements[loggingElement.cid].auxData.cards_viewed;
    i -= 1
  ) {
    if (DomUtil.isElementInViewport(trackedElement.children()[i])) {
      this._moduleViewElements[loggingElement.cid].auxData.cards_viewed = i + 1;
      return;
    }
  }
};

/**
 * Log a module view event (VIEW_BEGIN, VIEW_END, PIN_CARD_VIEW) event on a given element
 *
 * @param {Object} element the element to log the event for
 * @param {PCONST.thrift.EventType} viewType the type of event to log
 *                                        possible values: VIEW_BEGIN, VIEW_END, PIN_CARD_VIEW
 * @private
 */
ContextLogger.prototype._logModuleViewEvent = function(element, viewType) {
  if (!this._moduleViewElements[element.cid]) {
    return;
  }

  const loggerElement = element.parentMod || element;

  loggerElement._logCustomContextEvent(
    viewType,
    this._moduleViewElements[element.cid].elementType,
    this._moduleViewElements[element.cid].componentType,
    this._moduleViewElements[element.cid].auxData,
  );
  if (viewType === PCONST.thrift.EventType.PIN_CARD_VIEW) {
    this._moduleViewElements[element.cid].hasBeenViewed = true;
  } else {
    this._moduleViewElements[element.cid].isVisible = !this._moduleViewElements[element.cid]
      .isVisible;
  }
};

/**
 * Check whether a given element is visible
 * and log a corresponding module view event if a change occurred
 *
 * @param {Object} element the element to check
 * @param {boolean=} isModuleView whether we are logging PIN_CARD_VIEW events (default false)
 * @private
 */
ContextLogger.prototype._checkInViewport = function(element, isModuleView) {
  if (isModuleView) {
    if (
      this._moduleViewElements[element.cid] &&
      !this._moduleViewElements[element.cid].hasBeenViewed &&
      DomUtil.isElementInViewport(element.el)
    ) {
      this._logModuleViewEvent(element, PCONST.thrift.EventType.PIN_CARD_VIEW);
    }
  } else {
    const viewElement = this._moduleViewElements[element.cid];
    if (!viewElement) {
      return;
    }

    if (DomUtil.isElementInViewport(element.el)) {
      if (!viewElement.isVisible) {
        this._logModuleViewEvent(element, PCONST.thrift.EventType.VIEW_BEGIN);
      }
    } else if (viewElement.isVisible) {
      this._logModuleViewEvent(element, PCONST.thrift.EventType.VIEW_END);
    }
  }
};

/**
 * Log a VIEW_BEGIN or VIEW_END for a corresponding modal open or close event
 *
 * @param {Object} element the element to log around
 * @param {string} channel the pubSub channel
 * @param {string} topic the pubSub topic
 * @param {Object=} viewData data in the message
 * @private
 */
ContextLogger.prototype._onModalViewChange = function(element, channel, topic, viewData) {
  viewData = viewData || {};
  const action = viewData.action;
  const viewElement = this._moduleViewElements[element.cid];
  if (!viewElement) {
    return;
  }

  if (action && DomUtil.isElementInViewport(element.el)) {
    if (action === CONST.ViewActions.OPEN && viewElement.isVisible) {
      this._logModuleViewEvent(element, PCONST.thrift.EventType.VIEW_END);
    } else if (action === CONST.ViewActions.CLOSE && !viewElement.isVisible) {
      this._logModuleViewEvent(element, PCONST.thrift.EventType.VIEW_BEGIN);
    }
  }
};

/**
 * Clear the current view context.
 */
ContextLogger.prototype.clearContext = function() {
  this._currentViewContext = null;
};

/**
 * Adds a view context
 */
ContextLogger.prototype.addViewToContext = function(viewInfo) {
  const viewType = viewInfo.view_type;
  const viewParameter = viewInfo.view_parameter;
  if (!this._currentViewContext) {
    this._currentViewContext = contextEvent.fromPlainObject({});
  }
  this._currentViewContext.setViewType(viewType);
  this._currentViewContext.setViewParameter(viewParameter);
};

/**
 * Get previous view from context
 */
ContextLogger.prototype.getPreviousViewFromContext = function() {
  if (this._previousViewContext) {
    return {
      view_type: this._previousViewContext.getViewType(),
      view_parameter: this._previousViewContext.getViewParameter(),
    };
  } else {
    return {};
  }
};

/**
 * Get view from context
 */
ContextLogger.prototype.getViewFromContext = function() {
  if (this._currentViewContext) {
    return {
      view_type: this._currentViewContext.getViewType(),
      view_parameter: this._currentViewContext.getViewParameter(),
    };
  } else {
    return {};
  }
};

/**
 * Adds a search identifier (for visual search)
 */
ContextLogger.prototype.addSearchIdentifier = function(searchIdentifier) {
  if (!this._currentViewContext) {
    this._currentViewContext = contextEvent.fromPlainObject({});
  }
  this._currentViewContext.setViewData({ search_identifier: searchIdentifier });
};

/**
 * Check if the Backbone event was already used to create a ContextEvent.
 *
 * @param {Object} evt Backbone event to check.
 * @return {boolean} Whether a ContextEvent was already logged for the
 *     Backbone event.
 */
ContextLogger.prototype.eventLogged = function(evt) {
  return this._lastLoggedEvent === evt.originalEvent;
};

/**
 * Send all the events in the buffer to the server.
 * @param {?boolean} sync Whether the request should be synchronous.
 * @param {?boolean} resetShortOnly Whether we should reset short timeout only.
 */
ContextLogger.prototype.flushEvents = function(sync, resetShortOnly) {
  if (!this._events.length) {
    return;
  }

  this._handleDatelineCrossover();

  const inUserIdStrExperiment = experiments
    .getInstance()
    .v2ActivateExperiment('context_log_useridstr');
  const state = store.instance.getState();
  const serializableEvents = this._events.map((e) => {
    const serialized = e.toSerializable();

    if (inUserIdStrExperiment && inUserIdStrExperiment.startsWith('enabled')) {
      if (state.viewer && state.viewer.isAuth) {
        serialized.userIdStr = state.viewer.id;
      }
    }

    return serialized;
  });

  if (resetShortOnly) {
    this._resetShortTimerEvents();
  } else {
    this._reset();
  }

  // TODO(@dwest): Figure out why we're eventsResource is null sometimes. Either we shouldn't be calling this
  // function, or this._events.length should always be 0 in contexts where ContextLogResource is null
  try {
    const eventsResource = ResourceFactory.create('ContextLogResource', {
      events: serializableEvents,
      report_time: currentNs(),
      stage: context.initialPageContext.stage,
      appVersion: context.initialPageContext.appVersion,
    });

    eventsResource.callCreate(
      {
        async: !sync,
        showError: process.env.NODE_ENV === 'development',
      },
      { retries: 3 },
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e); // Seeing an error sometimes in tests. Instead of blowing up our
  }
};

/**
 * Reset the events to log.
 * @private
 */
ContextLogger.prototype._reset = function() {
  this._resetShortTimerEvents();
  this._resetLongTimerEvents();
};

/**
 * Reset the short timer events to log.
 * @private
 */
ContextLogger.prototype._resetShortTimerEvents = function() {
  this._events = [];
  clearTimeout(this._logTimeoutShort);
  this._logTimeoutShort = null;
};

/**
 * Reset the long timer events to log.
 * @private
 */
ContextLogger.prototype._resetLongTimerEvents = function() {
  this._events = [];
  clearTimeout(this._logTimeoutLong);
  this._logTimeoutLong = null;
};

contextLogger = ContextLogger;

/**
 * @type {ContextLogger} instance of contextLogger
 * @private
 */
let _contextLoggerInstance = null;

/**
 * Get the ContextLogger singleton.
 * @return {!ContextLogger}
 */
contextLogger.getInstance = function() {
  if (!_contextLoggerInstance) {
    _contextLoggerInstance = new ContextLogger();

    pubSub.subscribe(
      PCONST.PUBSUB_CHANNEL_CONTEXT_LOGGING,
      PCONST.PUBSUB_TOPIC_CONTEXT_LOG_PLAIN_OBJECT,
      (channel, topic, plainObject) => {
        if (!(contextEvent.KEY_EVENT_TYPE in plainObject)) {
          return;
        }
        const _contextEvent = contextEvent.fromPlainObject(plainObject);
        if ('aux_data' in plainObject) {
          _contextEvent.updateAuxData(plainObject.aux_data);
        }
        contextLogger.getInstance().addEvent(_contextEvent);
      },
    );

    pubSub.subscribe(
      PCONST.PUBSUB_CHANNEL_SITE,
      PCONST.PUBSUB_TOPIC_WINDOW_VISIBILITY_FOCUS,
      () => {
        const logger = contextLogger.getInstance();

        if (logger._clickthroughStartEvent) {
          const startEvent = logger._clickthroughStartEvent;
          logger._clickthroughStartEvent = null;
          const endEvent = contextEvent.clone(startEvent);
          endEvent.setEventType(PCONST.thrift.EventType.PIN_CLICKTHROUGH_END);
          endEvent.setDurationNs(currentNs() - startEvent.getTime());
          logger.addEvent(endEvent);
        }
      },
    );
  }

  return _contextLoggerInstance;
};

// eslint-disable-next-line pinterest/no-referencing-p-properties
export default P.expose('contextLogger', contextLogger);
